import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Check } from "lucide-react";

const STEPS = [
  "01 BASIC", "02 LOOKS", "03 EXPERIENCE", "04 SKILLS",
  "05 PORTFOLIO", "06 AVAILABILITY", "07 RATE CARD", "08 REVIEW",
];

const CATS = ["Model", "Actor", "Creator", "Dancer", "Fitness", "Anchor"];
const SKILLS = ["Acting", "Dancing", "Modelling", "Fitness", "Swimming", "Horse Riding", "Driving"];

const input = "w-full px-4 py-3 bg-[#1A1A1F] border border-[#5A2E2E] rounded-lg outline-none focus:border-[#C5A059]";

export default function OnboardingBiodata() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    full_name: "", display_name: "", age: "", gender: "female", city: "", location: "", nationality: "Indian", languages: "",
    height: "", chest: "", waist: "", hips: "", shoe_size: "", clothing_size: "", hair: "", eyes: "", skin_tone: "", features: "",
    experience_years: "", previous_work: "", music_videos: "", films: "", ott: "", tv: "", commercials: "", fashion: "", editorial: "", ecommerce: "", events: "", brand_campaigns: "",
    skills: [], instagram: "", youtube: "", imdb: "", other_links: "",
    profile_photo: "", closeup_photo: "", fullength_photo: "", gallery: [], showreel: "", intro_video: "",
    available_now: true, available_from: "", preferred_cities: "", travel: true, notice_period: "",
    categories: ["Model"],
    rate_card: { music_video: "", commercial: "", fashion: "", ecommerce: "", editorial: "", events: "", full_day: "", half_day: "" },
    about: "",
  });

  useEffect(() => {
    if (!user) return;
    if (user.profile_locked) { toast.error("Complete membership to unlock the profile builder."); nav("/pricing"); return; }
    api.get("/profile/me").then((r) => { if (r.data?.data && Object.keys(r.data.data).length) setData({ ...data, ...r.data.data }); });
  }, [user]);

  const set = (k, v) => setData({ ...data, [k]: v });
  const setRate = (k, v) => setData({ ...data, rate_card: { ...data.rate_card, [k]: v } });
  const toggleSkill = (s) => set("skills", data.skills.includes(s) ? data.skills.filter((x) => x !== s) : [...data.skills, s]);
  const toggleCat = (s) => set("categories", data.categories.includes(s) ? data.categories.filter((x) => x !== s) : [...data.categories, s]);

  const handleUpload = (key, multi = false) => async (e) => {
    const files = [...e.target.files];
    const readers = files.map((f) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f); }));
    const urls = await Promise.all(readers);
    if (multi) set(key, [...(data[key] || []), ...urls].slice(0, 12));
    else set(key, urls[0]);
  };

  const save = async (submit = false) => {
    try {
      await api.post("/profile/save", { data, submit_for_review: submit });
      toast.success(submit ? "Submitted for verification." : "Draft saved.");
      if (submit) { await refresh(); nav("/dashboard/talent"); }
    } catch (e) { toast.error("Could not save."); }
  };

  const next = () => setStep(Math.min(step + 1, STEPS.length - 1));
  const prev = () => setStep(Math.max(step - 1, 0));

  return (
    <div className="min-h-screen bg-[#0A0A0B]">
      <Header />
      <div className="max-w-5xl mx-auto px-4 py-12">
        <div className="sv-overline mb-3">Professional Biodata</div>
        <h1 className="font-serif-hero text-3xl lg:text-5xl mb-8">Build your SV Talent profile.</h1>

        <div className="flex overflow-x-auto gap-2 mb-10 pb-2">
          {STEPS.map((s, i) => (
            <button key={s} onClick={() => setStep(i)} data-testid={`step-${i}`}
              className={`px-4 py-2 rounded-full text-[10px] font-mono-label whitespace-nowrap ${i === step ? "bg-[#C5A059] text-[#0A0A0B]" : i < step ? "bg-[#5A2E2E] text-[#F4EFE4]" : "bg-[#1A1A1F] border border-[#5A2E2E] text-[#8A8478]"}`}>
              {s}
            </button>
          ))}
        </div>

        <div className="sv-card p-6 sm:p-10">
          {step === 0 && (
            <div className="grid md:grid-cols-2 gap-4">
              <input className={input} placeholder="Full Name" value={data.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="bio-full-name" />
              <input className={input} placeholder="Display / Professional Name" value={data.display_name} onChange={(e) => set("display_name", e.target.value)} />
              <input className={input} placeholder="Age" type="number" value={data.age} onChange={(e) => set("age", e.target.value)} />
              <select className={input} value={data.gender} onChange={(e) => set("gender", e.target.value)}>
                <option value="female">Female</option><option value="male">Male</option><option value="other">Other</option>
              </select>
              <input className={input} placeholder="City" value={data.city} onChange={(e) => set("city", e.target.value)} />
              <input className={input} placeholder="Current Location" value={data.location} onChange={(e) => set("location", e.target.value)} />
              <input className={input} placeholder="Nationality" value={data.nationality} onChange={(e) => set("nationality", e.target.value)} />
              <input className={input} placeholder="Languages (comma separated)" value={data.languages} onChange={(e) => set("languages", e.target.value)} />
              <div className="md:col-span-2">
                <div className="font-mono-label text-[10px] mb-2 text-[#C5A059]">Categories</div>
                <div className="flex flex-wrap gap-2">
                  {CATS.map((c) => (
                    <button type="button" key={c} onClick={() => toggleCat(c)}
                      className={`px-4 py-2 rounded-full text-xs border ${data.categories.includes(c) ? "bg-[#F4EFE4] text-[#0A0A0B] border-[#F4EFE4]" : "border-[#5A2E2E]"}`}>{c}</button>
                  ))}
                </div>
              </div>
              <textarea className={`${input} md:col-span-2 min-h-[100px]`} placeholder="About / Bio" value={data.about} onChange={(e) => set("about", e.target.value)} />
            </div>
          )}
          {step === 1 && (
            <div className="grid md:grid-cols-2 gap-4">
              {["height", "chest", "waist", "hips", "shoe_size", "clothing_size", "hair", "eyes", "skin_tone", "features"].map((f) => (
                <input key={f} className={input} placeholder={f.replace("_", " ").toUpperCase()} value={data[f]} onChange={(e) => set(f, e.target.value)} />
              ))}
            </div>
          )}
          {step === 2 && (
            <div className="grid md:grid-cols-2 gap-4">
              <input className={input} placeholder="Years of Experience" value={data.experience_years} onChange={(e) => set("experience_years", e.target.value)} />
              <input className={input} placeholder="Previous Work Highlight" value={data.previous_work} onChange={(e) => set("previous_work", e.target.value)} />
              {["music_videos", "films", "ott", "tv", "commercials", "fashion", "editorial", "ecommerce", "events", "brand_campaigns"].map((f) => (
                <input key={f} className={input} placeholder={f.replace("_", " ")} value={data[f]} onChange={(e) => set(f, e.target.value)} />
              ))}
            </div>
          )}
          {step === 3 && (
            <div>
              <div className="font-mono-label text-[10px] mb-3 text-[#C5A059]">Skills</div>
              <div className="flex flex-wrap gap-2 mb-6">
                {SKILLS.map((s) => (
                  <button key={s} type="button" onClick={() => toggleSkill(s)}
                    className={`px-4 py-2 rounded-full text-xs border ${data.skills.includes(s) ? "bg-[#C5A059] text-[#0A0A0B] border-[#C5A059]" : "border-[#5A2E2E]"}`}>{s}</button>
                ))}
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <input className={input} placeholder="Instagram" value={data.instagram} onChange={(e) => set("instagram", e.target.value)} />
                <input className={input} placeholder="YouTube" value={data.youtube} onChange={(e) => set("youtube", e.target.value)} />
                <input className={input} placeholder="IMDb (if any)" value={data.imdb} onChange={(e) => set("imdb", e.target.value)} />
                <input className={input} placeholder="Other Link" value={data.other_links} onChange={(e) => set("other_links", e.target.value)} />
              </div>
            </div>
          )}
          {step === 4 && (
            <div className="space-y-6">
              <div className="grid md:grid-cols-3 gap-4">
                {["profile_photo", "closeup_photo", "fullength_photo"].map((k) => (
                  <label key={k} className="block border-2 border-dashed border-[#5A2E2E] rounded-xl p-4 text-center cursor-pointer hover:border-[#C5A059]">
                    <input type="file" accept="image/*" className="hidden" onChange={handleUpload(k)} />
                    {data[k] ? <img src={data[k]} className="rounded-lg object-cover aspect-[3/4] w-full" alt="" /> : <div className="py-10 text-xs font-mono-label text-[#8A8478]">Upload {k.replace("_", " ")}</div>}
                  </label>
                ))}
              </div>
              <label className="block border-2 border-dashed border-[#5A2E2E] rounded-xl p-6 text-center cursor-pointer">
                <input type="file" accept="image/*" multiple className="hidden" onChange={handleUpload("gallery", true)} />
                <div className="font-mono-label text-[10px] text-[#8A8478]">Add Gallery Images (multiple)</div>
                {data.gallery?.length > 0 && (
                  <div className="grid grid-cols-4 gap-2 mt-4">
                    {data.gallery.map((g, i) => <img key={i} src={g} className="rounded aspect-square object-cover" alt="" />)}
                  </div>
                )}
              </label>
              <input className={input} placeholder="Showreel URL (YouTube/Vimeo)" value={data.showreel} onChange={(e) => set("showreel", e.target.value)} />
              <input className={input} placeholder="Intro Video URL" value={data.intro_video} onChange={(e) => set("intro_video", e.target.value)} />
            </div>
          )}
          {step === 5 && (
            <div className="grid md:grid-cols-2 gap-4">
              <label className="flex items-center gap-3 px-4 py-3 bg-[#1A1A1F] border border-[#5A2E2E] rounded-lg">
                <input type="checkbox" checked={data.available_now} onChange={(e) => set("available_now", e.target.checked)} /> Available Now
              </label>
              <input className={input} placeholder="Available From (YYYY-MM-DD)" value={data.available_from} onChange={(e) => set("available_from", e.target.value)} />
              <input className={input} placeholder="Preferred Cities" value={data.preferred_cities} onChange={(e) => set("preferred_cities", e.target.value)} />
              <label className="flex items-center gap-3 px-4 py-3 bg-[#1A1A1F] border border-[#5A2E2E] rounded-lg">
                <input type="checkbox" checked={data.travel} onChange={(e) => set("travel", e.target.checked)} /> Willing to Travel
              </label>
              <input className={input} placeholder="Notice Period" value={data.notice_period} onChange={(e) => set("notice_period", e.target.value)} />
            </div>
          )}
          {step === 6 && (
            <div>
              <p className="text-sm text-[#8A8478] mb-4">Indicative starting rates. Negotiable per project. Not a guarantee of earnings.</p>
              <div className="grid md:grid-cols-2 gap-4">
                {Object.keys(data.rate_card).map((k) => (
                  <input key={k} className={input} placeholder={k.replace("_", " ").toUpperCase() + " (₹)"} value={data.rate_card[k]} onChange={(e) => setRate(k, e.target.value)} />
                ))}
              </div>
            </div>
          )}
          {step === 7 && (
            <div className="space-y-6">
              <div className="grid md:grid-cols-4 gap-4 items-start">
                <img src={data.profile_photo || "https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=400"} className="rounded-xl aspect-[3/4] object-cover" alt="" />
                <div className="md:col-span-3">
                  <div className="sv-overline mb-2">{data.categories.join(" · ") || "Talent"}</div>
                  <h2 className="font-serif-hero text-4xl">{data.display_name || data.full_name || "Your talent profile will appear here after submission and approval."}</h2>
                  <div className="text-sm text-[#8A8478] mt-1">{data.city} · {data.age} yrs · {data.gender}</div>
                  <p className="mt-4 text-[#CDC7BA]">{data.about}</p>
                  <div className="mt-4 font-mono-label text-[10px] text-[#C5A059]">Skills</div>
                  <div className="text-sm">{data.skills.join(" · ")}</div>
                  <div className="mt-2 font-mono-label text-[10px] text-[#C5A059]">Height / Measurements</div>
                  <div className="text-sm">{data.height} · {data.chest}–{data.waist}–{data.hips}</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-3">
                <button onClick={() => setStep(0)} className="sv-btn-outline" data-testid="review-edit">Edit Profile</button>
                <button onClick={() => save(false)} className="sv-btn-outline" data-testid="review-save-draft">Save Draft</button>
                <button onClick={() => save(true)} className="sv-btn-primary" data-testid="review-submit">Submit for Review</button>
              </div>
            </div>
          )}

          {step < 7 && (
            <div className="mt-8 flex justify-between">
              <button onClick={prev} disabled={step === 0} className="sv-btn-outline inline-flex items-center gap-2 disabled:opacity-40"><ChevronLeft size={14} />Back</button>
              <div className="flex gap-3">
                <button onClick={() => save(false)} className="sv-btn-outline" data-testid="wizard-save">Save Draft</button>
                <button onClick={next} className="sv-btn-primary inline-flex items-center gap-2" data-testid="wizard-next">Next<ChevronRight size={14} /></button>
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
